import { useEffect, useState } from 'react'
import { X, Shield, ArrowLeftRight, Truck, Search, Package, ChevronRight, ChevronLeft, AlertCircle, RotateCcw } from 'lucide-react'
import api from '@/lib/api'

type CaseType = 'warranty' | 'doa' | 'transport' | 'return'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  forceCaseType?: CaseType
}

interface OrderData {
  order_id: number
  order_reference: string
  order_date: string
  customer_id: number
  customer_email: string
  customer_name: string
  customer_phone: string
  customer_type: string
  customer_nif: string
  products: Array<{
    product_id: number
    product_name: string
    product_reference: string
    image_url: string
    quantity: number
    unit_price: number
  }>
  shipping_address: {
    street: string
    city: string
    postal_code: string
    country_code: string
    phone: string
  }
}

const CASE_TYPES: { id: CaseType; label: string; icon: React.ElementType; description: string; color: string }[] = [
  { id: 'warranty', label: 'Garantia', icon: Shield, description: 'Cliente envia producto defectuoso, se evalua y si procede se envia sustitucion', color: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20' },
  { id: 'doa', label: 'Cam-falla (DOA)', icon: ArrowLeftRight, description: 'Producto llega con problemas, se envia sustitucion inmediata y se recoge el defectuoso', color: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20' },
  { id: 'transport', label: 'Rotura/Perdida', icon: Truck, description: 'Producto roto o perdido en transporte, se envia sustitucion sin recogida', color: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20' },
]

export default function CreateIncidentModal({ isOpen, onClose, onSuccess, forceCaseType }: Props) {
  // Cuando forceCaseType viene informado, saltamos el paso 1 (selección de tipo)
  const initialStep = forceCaseType ? 2 : 1
  const [step, setStep] = useState(initialStep)
  const [caseType, setCaseType] = useState<CaseType | null>(forceCaseType ?? null)

  useEffect(() => {
    if (isOpen) {
      setStep(forceCaseType ? 2 : 1)
      setCaseType(forceCaseType ?? null)
    }
  }, [isOpen, forceCaseType])

  const isManualReturn = caseType === 'return'
  const modalTitle = isManualReturn ? 'Nueva devolución manual' : 'Nueva incidencia'

  // Step 2: Order search
  const [orderRef, setOrderRef] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [orderData, setOrderData] = useState<OrderData | null>(null)

  // Step 3: Product selection (idx → unidades a incluir)
  const [selectedProducts, setSelectedProducts] = useState<Record<number, number>>({})

  // Step 4: Details
  const [reasonDetails, setReasonDetails] = useState('')
  const [replacementCarrier, setReplacementCarrier] = useState('')
  const [replacementTracking, setReplacementTracking] = useState('')
  const [carrierClaimRef, setCarrierClaimRef] = useState('')
  const [internalNotes, setInternalNotes] = useState('')

  // Transport-specific fields
  const [incidentCarrier, setIncidentCarrier] = useState('')
  const [incidentSubtype, setIncidentSubtype] = useState('')
  const [incidentSubtypeOther, setIncidentSubtypeOther] = useState('')
  const [incidentTracking, setIncidentTracking] = useState('')

  // Return manual: recogida automática (desactivado por defecto)
  const [autoPickup, setAutoPickup] = useState(false)

  // Submit
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const searchOrder = async () => {
    if (!orderRef.trim()) return
    setSearching(true)
    setSearchError('')
    try {
      // Para devoluciones manuales, usar el endpoint admin que salta la validación de plazo.
      // Para garantías/DOA/transporte, el endpoint público ya no aplica el plazo de 14 días.
      const endpoint = caseType === 'return' ? '/returns/admin/validate-order' : '/returns/validate-order'
      const response = await api.post(endpoint, {
        order_reference: orderRef.trim(),
        customer_email: customerEmail.trim() || undefined,
        case_type: caseType || 'return',
      })
      setOrderData(response.data)
      setSelectedProducts({})
      setStep(3)
    } catch (error: any) {
      const detail = error.response?.data?.detail
      if (typeof detail === 'object' && detail?.message) {
        setSearchError(detail.message)
      } else if (typeof detail === 'string') {
        setSearchError(detail)
      } else {
        setSearchError('Error buscando pedido')
      }
    } finally {
      setSearching(false)
    }
  }

  const toggleProduct = (idx: number) => {
    setSelectedProducts(prev => {
      if (idx in prev) {
        const { [idx]: _removed, ...rest } = prev
        return rest
      }
      // Si la línea tiene varias unidades, por defecto se incluye solo 1
      return { ...prev, [idx]: 1 }
    })
  }

  const updateProductQuantity = (idx: number, newQty: number, maxQty: number) => {
    const qty = Math.max(1, Math.min(newQty, maxQty))
    setSelectedProducts(prev => ({ ...prev, [idx]: qty }))
  }

  const selectedCount = Object.keys(selectedProducts).length

  const handleSubmit = async () => {
    if (!caseType || !orderData || selectedCount === 0) return
    setSubmitting(true)
    setSubmitError('')

    try {
      const products = Object.entries(selectedProducts).map(([idxStr, qty]) => {
        const p = orderData.products[Number(idxStr)]
        return {
          product_id: p.product_id,
          product_name: p.product_name,
          product_reference: p.product_reference,
          image_url: p.image_url,
          quantity: qty,
          unit_price: p.unit_price,
        }
      })

      await api.post('/returns/admin/incidents', {
        case_type: caseType,
        order_reference: orderData.order_reference,
        customer_email: orderData.customer_email,
        customer_name: orderData.customer_name,
        customer_phone: orderData.customer_phone,
        customer_type: orderData.customer_type,
        customer_nif: orderData.customer_nif,
        products,
        reason_details: reasonDetails || undefined,
        pickup_address: orderData.shipping_address ? {
          street: orderData.shipping_address.street,
          city: orderData.shipping_address.city,
          postal_code: orderData.shipping_address.postal_code,
          country_code: orderData.shipping_address.country_code || 'ES',
          phone: orderData.shipping_address.phone,
        } : undefined,
        replacement_carrier: caseType !== 'doa' && caseType !== 'return' ? (replacementCarrier || undefined) : undefined,
        replacement_tracking: caseType !== 'doa' && caseType !== 'return' ? (replacementTracking || undefined) : undefined,
        carrier_claim_reference: carrierClaimRef || undefined,
        internal_notes: internalNotes || undefined,
        order_id: orderData.order_id,
        order_date: orderData.order_date,
        customer_id: orderData.customer_id,
        incident_carrier: incidentCarrier || undefined,
        incident_subtype: incidentSubtype || undefined,
        incident_subtype_other: incidentSubtypeOther || undefined,
        incident_tracking: incidentTracking || undefined,
        auto_pickup: caseType === 'return' ? autoPickup : undefined,
      })

      onSuccess()
    } catch (error: any) {
      const detail = error.response?.data?.detail
      const fallback = isManualReturn ? 'Error creando devolución' : 'Error creando incidencia'
      setSubmitError(typeof detail === 'string' ? detail : detail?.error || fallback)
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">{modalTitle}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Paso {step} de 4</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {/* Step 1: Select type */}
          {step === 1 && (
            <div className="space-y-3">
              <h3 className="font-medium text-slate-900 dark:text-white mb-4">Tipo de incidencia</h3>
              {CASE_TYPES.map(ct => {
                const Icon = ct.icon
                const selected = caseType === ct.id
                return (
                  <button
                    key={ct.id}
                    onClick={() => setCaseType(ct.id)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      selected
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : `${ct.color} hover:border-slate-300 dark:hover:border-slate-600`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${selected ? 'text-indigo-600' : 'text-slate-500'}`} />
                      <div>
                        <p className={`font-medium ${selected ? 'text-indigo-900 dark:text-indigo-200' : 'text-slate-900 dark:text-white'}`}>{ct.label}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{ct.description}</p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {/* Step 2: Search order */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900 dark:text-white">Buscar pedido o factura</h3>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">N. pedido / factura</label>
                <input
                  type="text"
                  value={orderRef}
                  onChange={(e) => setOrderRef(e.target.value)}
                  placeholder="Ej: 105432"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  onKeyDown={(e) => e.key === 'Enter' && searchOrder()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email del cliente (opcional)</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="cliente@email.com"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  onKeyDown={(e) => e.key === 'Enter' && searchOrder()}
                />
              </div>
              {searchError && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {searchError}
                </div>
              )}
              <button
                onClick={searchOrder}
                disabled={searching || !orderRef.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {searching ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                {searching ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          )}

          {/* Step 3: Select products */}
          {step === 3 && orderData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-900 dark:text-white">Seleccionar productos</h3>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Pedido #{orderData.order_reference} - {orderData.customer_name}
                </span>
              </div>
              <div className="space-y-2">
                {orderData.products.map((product, idx) => {
                  const isSelected = idx in selectedProducts
                  const selectedQty = selectedProducts[idx] ?? 1
                  return (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border transition-all ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleProduct(idx)}
                          className="w-4 h-4 text-indigo-600 rounded border-slate-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{product.product_name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {product.product_reference} | x{product.quantity} | {product.unit_price.toFixed(2)} EUR
                          </p>
                        </div>
                      </label>
                      {/* Selector de unidades cuando la línea tiene más de 1 */}
                      {isSelected && product.quantity > 1 && (
                        <div className="flex items-center gap-3 mt-2 ml-7">
                          <span className="text-xs text-slate-600 dark:text-slate-400">Unidades a incluir:</span>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => updateProductQuantity(idx, selectedQty - 1, product.quantity)}
                              className="w-7 h-7 rounded-lg bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 flex items-center justify-center text-slate-700 dark:text-slate-200 font-bold text-sm"
                            >
                              -
                            </button>
                            <span className="w-8 text-center font-semibold text-sm text-slate-900 dark:text-white">{selectedQty}</span>
                            <button
                              type="button"
                              onClick={() => updateProductQuantity(idx, selectedQty + 1, product.quantity)}
                              className="w-7 h-7 rounded-lg bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 flex items-center justify-center text-slate-700 dark:text-slate-200 font-bold text-sm"
                            >
                              +
                            </button>
                          </div>
                          <span className="text-xs text-slate-400">de {product.quantity}</span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step 4: Details & Confirm */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900 dark:text-white">Detalles de la incidencia</h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Descripcion del problema</label>
                <textarea
                  value={reasonDetails}
                  onChange={(e) => setReasonDetails(e.target.value)}
                  rows={3}
                  placeholder="Describe el problema..."
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>

              {/* Transport incident details */}
              {caseType === 'transport' && (
                <div className="space-y-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm font-medium text-red-900 dark:text-red-200">Datos del envio con incidencia</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-red-700 dark:text-red-300 mb-1">Transportista causante *</label>
                      <select
                        value={incidentCarrier}
                        onChange={(e) => setIncidentCarrier(e.target.value)}
                        className="w-full px-3 py-2 border border-red-200 dark:border-red-700 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      >
                        <option value="">Seleccionar</option>
                        <option value="mrw">MRW</option>
                        <option value="gls">GLS</option>
                        <option value="ctt">CTT</option>
                        <option value="dhl">DHL</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-red-700 dark:text-red-300 mb-1">Tipo de incidencia *</label>
                      <select
                        value={incidentSubtype}
                        onChange={(e) => setIncidentSubtype(e.target.value)}
                        className="w-full px-3 py-2 border border-red-200 dark:border-red-700 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      >
                        <option value="">Seleccionar</option>
                        <option value="loss">Perdida</option>
                        <option value="damage">Rotura</option>
                        <option value="other">Otra</option>
                      </select>
                    </div>
                  </div>
                  {incidentSubtype === 'other' && (
                    <div>
                      <label className="block text-xs text-red-700 dark:text-red-300 mb-1">Especificar tipo *</label>
                      <input
                        type="text"
                        value={incidentSubtypeOther}
                        onChange={(e) => setIncidentSubtypeOther(e.target.value)}
                        placeholder="Describe el tipo de incidencia"
                        className="w-full px-3 py-2 border border-red-200 dark:border-red-700 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-red-700 dark:text-red-300 mb-1">N. seguimiento del envio con incidencia *</label>
                    <input
                      type="text"
                      value={incidentTracking}
                      onChange={(e) => setIncidentTracking(e.target.value)}
                      placeholder="Tracking del envio original"
                      className="w-full px-3 py-2 border border-red-200 dark:border-red-700 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                    />
                  </div>
                </div>
              )}

              {/* DOA: MRW Canje info */}
              {caseType === 'doa' && (
                <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
                  <p className="text-sm font-medium text-teal-900 dark:text-teal-200 flex items-center gap-2">
                    <ArrowLeftRight className="w-4 h-4" />
                    MRW Canje
                  </p>
                  <p className="text-xs text-teal-700 dark:text-teal-300 mt-1.5">
                    Se creara automaticamente un envio MRW Canje. El mensajero entregara la sustitucion y recogera el producto defectuoso en la misma visita. El tracking se generara automaticamente.
                  </p>
                </div>
              )}

              {/* Transport: manual replacement carrier/tracking */}
              {caseType === 'transport' && (
                <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Envio de sustitucion</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-blue-700 dark:text-blue-300 mb-1">Transportista</label>
                      <select
                        value={replacementCarrier}
                        onChange={(e) => setReplacementCarrier(e.target.value)}
                        className="w-full px-3 py-2 border border-blue-200 dark:border-blue-700 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      >
                        <option value="">Seleccionar</option>
                        <option value="mrw">MRW</option>
                        <option value="gls">GLS</option>
                        <option value="ctt">CTT</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-blue-700 dark:text-blue-300 mb-1">N. seguimiento</label>
                      <input
                        type="text"
                        value={replacementTracking}
                        onChange={(e) => setReplacementTracking(e.target.value)}
                        className="w-full px-3 py-2 border border-blue-200 dark:border-blue-700 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Carrier claim for Transport */}
              {caseType === 'transport' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Ref. reclamacion transportista</label>
                  <input
                    type="text"
                    value={carrierClaimRef}
                    onChange={(e) => setCarrierClaimRef(e.target.value)}
                    placeholder="Referencia de la reclamacion"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
              )}

              {/* Return manual: aviso + checkbox recogida automática */}
              {isManualReturn && (
                <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800 space-y-3">
                  <p className="text-sm font-medium text-violet-900 dark:text-violet-200 flex items-center gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Devolución manual
                  </p>
                  <p className="text-xs text-violet-700 dark:text-violet-300">
                    Esta devolución se dará de alta SIN validar el plazo habitual de {14} días. Quedará en estado "Aprobada" lista para gestionar desde el listado.
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoPickup}
                      onChange={(e) => setAutoPickup(e.target.checked)}
                      className="w-4 h-4 text-violet-600 rounded border-slate-300"
                    />
                    <span className="text-sm text-violet-900 dark:text-violet-200">
                      Programar recogida automática con el transportista
                    </span>
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas internas</label>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  rows={2}
                  placeholder="Notas internas (no visibles para el cliente)"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>

              {submitError && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {submitError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={() => {
              const minStep = forceCaseType ? 2 : 1
              if (step > minStep) setStep(step - 1)
              else onClose()
            }}
            className="flex items-center gap-1.5 px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm font-medium"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === (forceCaseType ? 2 : 1) ? 'Cancelar' : 'Atras'}
          </button>

          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !caseType) ||
                (step === 2 && !orderData) ||
                (step === 3 && selectedCount === 0)
              }
              className="flex items-center gap-1.5 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Package className="w-4 h-4" />
              )}
              {submitting ? 'Creando...' : (isManualReturn ? 'Crear devolución' : 'Crear incidencia')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
